// Staff waiting screen (roadmap #41), route /staff/dashboard. The staff keeps this open
// to receive calls in the browser: it registers the web device (staff-join on the shared
// socket + FCM token) and routes incoming-call notifications to the ringing screen.
// Mirrors the always-logged-in /#/dashboard behaviour of the previous client.
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import BrandLockup from '../components/BrandLockup'
import LanguageSwitch from '../components/LanguageSwitch'
import { useNavigate } from 'react-router-dom'
import { socket, readyTheSocketConnection, onSocketConnected } from '../core/socket'
import { fetchIceConfig } from '../core/iceConfig'
import { globals } from '../core/globals'
import { getAuthUser } from '../core/auth'
import { staffLogout } from './staffAuth'
import { primeRingtone } from './ringtone'
import { t } from '../i18n'
import DesktopSetup from './DesktopSetup'
import { consumeIncomingCallLink } from './incomingCallLink'
import {
  primeIncomingCallFromCallId,
  processSilentNotification,
  registerFcmToken,
  registerMessagingServiceWorker,
  requestPushPermissionAndRegister,
  type PushRegistrationResult,
  subscribeForegroundMessages,
  subscribeServiceWorkerMessages,
} from './messaging'

const StaffDashboard = () => {
  const navigate = useNavigate()
  const user = getAuthUser()
  // A notification acted on with no window open lands HERE, carrying the call in the link
  // (see the service worker). Guarded so the call is opened once and never replayed by a
  // reload or a re-render.
  const notificationLinkHandled = useRef(false)
  const [connected, setConnected] = useState(socket.connected)
  const [notificationsReady, setNotificationsReady] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted'
  )
  const [enablingNotifications, setEnablingNotifications] = useState(false)
  // Why this device is not reachable yet (null while nothing is known to be wrong).
  const [notificationsProblem, setNotificationsProblem] = useState<PushRegistrationResult | null>(null)

  useEffect(() => {
    let cleanupSwMessages = () => {}

    const init = async () => {
      // ICE CONFIG IS NEEDED BEFORE THE FIRST CALL (THE PEER CONNECTION READS IT).
      fetchIceConfig()

      // READY THE SHARED SOCKET AND REGISTER THIS STAFF EMAIL (staff-join). RE-REGISTER ON
      // EVERY RECONNECT — THE SERVER ROUTES CALLS BY THE REGISTERED EMAIL.
      try {
        await readyTheSocketConnection()
      } catch (e) {
        console.log('socket not ready yet, will register on connect', e)
      }
      onSocketConnected()
      socket.on('connect', onSocketConnected)

      // REGISTER THE BACKGROUND SERVICE WORKER AND, WHEN NOTIFICATIONS ARE ALREADY ALLOWED, THE
      // WEB PUSH TOKEN. Permission itself is only ever asked from the button (a click).
      registerMessagingServiceWorker()
      if (user?.user_id) {
        registerFcmToken(user.user_id).then((result) => {
          if (result === 'registered') {
            setNotificationsReady(true)
            setNotificationsProblem(null)
          } else {
            setNotificationsReady(false)
            // "dismissed" here only means nobody has been asked yet: the button says enough.
            setNotificationsProblem(result === 'dismissed' ? null : result)
          }
        })
      }

      // FOREGROUND PUSHES AND SERVICE-WORKER BRIDGE BOTH OPEN THE INCOMING-CALL SCREEN.
      subscribeForegroundMessages((path, opts) => navigate(path, opts))
      cleanupSwMessages = subscribeServiceWorkerMessages(
        (path, opts) => navigate(path, opts),
        () => {}
      )

      // DEV-ONLY HOOK SO LOCAL E2E CAN INJECT AN INCOMING-CALL PUSH (FCM WEB PUSH IS NOT
      // DELIVERED TO HEADLESS BROWSERS — THE SAME STUB THE RESILIENCE HARNESS USED FOR THE
      // STAFF SIMULATOR). IT EXERCISES THE REAL processSilentNotification PATH.
      if (import.meta.env.DEV) {
        ;(window as any).__staffSimulateIncoming = (data: any) =>
          processSilentNotification(data, (path, opts) => navigate(path, opts))
      }
    }
    init()

    const onConnect = () => setConnected(true)
    const onDisconnect = () => setConnected(false)
    socket.on('connect', onConnect)
    socket.on('disconnect', onDisconnect)

    // Ring-when-unattended: browsers only let audio start on a page the user has
    // interacted with, and the incoming-call screen opens by itself from a push with
    // the window minimised. Prime the ringtone on the first interaction of this
    // waiting screen so the later, unattended play is allowed (see staff/ringtone.ts).
    const onFirstInteraction = () => {
      void primeRingtone()
    }
    window.addEventListener('pointerdown', onFirstInteraction, { once: true })
    window.addEventListener('keydown', onFirstInteraction, { once: true })

    return () => {
      window.removeEventListener('pointerdown', onFirstInteraction)
      window.removeEventListener('keydown', onFirstInteraction)
      socket.off('connect', onSocketConnected)
      socket.off('connect', onConnect)
      socket.off('disconnect', onDisconnect)
      cleanupSwMessages()
    }
  }, [])

  // OPEN THE CALL A NOTIFICATION WAS ACTED ON WHEN NO WINDOW WAS RUNNING.
  //
  // The service worker can only hand the call to an application window that already exists.
  // With none open it opens one, and the call travels in the link - which main.tsx captures at
  // boot, because the route guard and the sign-on round-trip drop it long before this screen
  // mounts. Here the call is rebuilt from its identifier and the ringing screen takes over,
  // auto-accepting when that is the button that was pressed.
  useEffect(() => {
    if (notificationLinkHandled.current) return
    const link = consumeIncomingCallLink()
    if (!link) return
    notificationLinkHandled.current = true

    ;(async () => {
      const opened = await primeIncomingCallFromCallId(link.callId)
      if (!opened) {
        // The call is over, or too old to answer. Say so and stay on the dashboard.
        toast(t.staffCallNoLongerActive)
        return
      }
      navigate('/staff/incomingcall', {
        replace: true,
        state: {
          callArrivedAt: Date.now(),
          autoAccept: link.action === 'CALL_ACCEPT',
          autoReject: link.action === 'CALL_REJECT',
        },
      })
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The button used to ignore every answer but "granted". Where the browser refuses
  // notifications without asking (the site was blocked earlier, or a private or guest window),
  // the click therefore did nothing at all and the device silently stayed unreachable. Every
  // outcome is now reported, and the button stays until the device is really registered.
  const enableNotifications = async () => {
    if (enablingNotifications || !user?.user_id) return
    setEnablingNotifications(true)
    const result = await requestPushPermissionAndRegister(user.user_id)
    setEnablingNotifications(false)
    setNotificationsProblem(result === 'registered' ? null : result)
    if (result === 'registered') {
      setNotificationsReady(true)
      toast.success(t.staffNotificationsEnabled)
    }
  }

  const onLogout = async () => {
    await staffLogout()
    navigate('/staff', { replace: true })
  }

  const staffName = user?.full_name || globals.email || ''
  // Calls reach this device by push alone, so a live connection is not enough to be "available":
  // without registered alerts the staff member is told so instead of being shown as reachable.
  const status = !connected ? 'offline' : notificationsReady ? 'online' : 'warning'

  return (
    <div className="cf-page">
      <div className="cf-card cf-staff-card">
        <BrandLockup />
        <div className={`cf-status-dot ${status}`} />
        <h1 className="cf-title">
          {status === 'online' ? t.staffAvailable : status === 'warning' ? t.staffNotReachable : t.staffReconnecting}
        </h1>
        <p className="cf-subtitle">
          {status === 'online'
            ? t.staffAvailableHint
            : status === 'warning'
              ? t.staffNotReachableHint
              : t.staffReconnectingHint}
        </p>
        {staffName && <p className="cf-staff-name">{staffName}</p>}

        {!notificationsReady && (
          <div className="cf-help">
            {t.staffNotificationsHelp}
            {notificationsProblem && (
              <p className="cf-staff-notifications-problem" role="alert">
                {notificationsProblem === 'denied'
                  ? t.staffNotificationsBlocked
                  : notificationsProblem === 'unsupported'
                    ? t.staffNotificationsUnsupported
                    : notificationsProblem === 'dismissed'
                      ? t.staffNotificationsDismissed
                      : t.staffNotificationsFailed}
              </p>
            )}
            {notificationsProblem !== 'unsupported' && (
              <button
                className="cf-btn cf-btn-secondary"
                onClick={enableNotifications}
                disabled={enablingNotifications}
              >
                {notificationsProblem === 'denied' || notificationsProblem === 'failed'
                  ? t.staffRetryNotifications
                  : t.staffEnableNotifications}
              </button>
            )}
          </div>
        )}

        <DesktopSetup />

        <button className="cf-btn cf-btn-secondary cf-staff-logout" onClick={onLogout}>
          {t.staffLogout}
        </button>

        <LanguageSwitch />
      </div>
    </div>
  )
}

export default StaffDashboard
